import { Component, ElementRef, HostBinding, OnDestroy, OnInit, QueryList, TemplateRef, ViewChild, ViewChildren } from '@angular/core';
import { DataService, MigrateContractorRegistrationsResult, ContractorRegistrationMigrationProcessing, WorkflowContextService } from 'src/app/services';
import { Client, ContractorType, Document } from 'src/app/models';
import { LayoutService } from 'src/app/services/layout.service';
import { TableColumn } from 'src/app/components/common/common-table/common-table.component';
import { CommonDropdownComponent } from 'src/app/components/common/common-dropdown/common-dropdown.component';
import { CommonDropdownData, DropdownOption } from 'src/app/components/common/common-dropdown/common-dropdown.component.model';
import {
  UploadComponent,
  UploadResponse
} from 'src/app/components/upload/upload.component';

@Component({
  selector: 'wm-contractor-registration-migration',
  templateUrl: './contractor-registration-migration.component.html',
  styleUrls: ['./contractor-registration-migration.component.css'],
  standalone: false
})
export class ContractorRegistrationMigrationComponent implements OnInit, OnDestroy {
  @ViewChild('migrationUploadComponent', { static: false })
  migrationUploadComponent!: UploadComponent;

  @ViewChildren(CommonDropdownComponent)
  migrationDropdowns!: QueryList<CommonDropdownComponent>;

  // Keep option identities stable so blur/change detection cannot replace a clicked row.
  jurisdictionOptions: DropdownOption[] = [];
  contractorTypeOptions: DropdownOption[] = [];

  @ViewChild('historyDate', { static: true })
  historyDate!: TemplateRef<{ $implicit: ContractorRegistrationMigrationProcessing }>;

  @ViewChild('historyType', { static: true })
  historyType!: TemplateRef<{ $implicit: ContractorRegistrationMigrationProcessing }>;

  @ViewChild('historyStatus', { static: true })
  historyStatus!: TemplateRef<{ $implicit: ContractorRegistrationMigrationProcessing }>;

  @ViewChild('historyCreated', { static: true })
  historyCreated!: TemplateRef<{ $implicit: ContractorRegistrationMigrationProcessing }>;

  @ViewChild('historyUpdated', { static: true })
  historyUpdated!: TemplateRef<{ $implicit: ContractorRegistrationMigrationProcessing }>;

  @ViewChild('historyRegistrationsCreated', { static: true })
  historyRegistrationsCreated!: TemplateRef<{ $implicit: ContractorRegistrationMigrationProcessing }>;

  @ViewChild('historyRegistrationsSkipped', { static: true })
  historyRegistrationsSkipped!: TemplateRef<{ $implicit: ContractorRegistrationMigrationProcessing }>;

  @ViewChild('historyRunBy', { static: true })
  historyRunBy!: TemplateRef<{ $implicit: ContractorRegistrationMigrationProcessing }>;

  @ViewChild('historyWarnings', { static: true })
  historyWarnings!: TemplateRef<{ $implicit: ContractorRegistrationMigrationProcessing }>;

  historyColumns: TableColumn[] = [];

  uploadKey: string = '';
  migrationFilePath: string | null = null;
  migrationFileUploaded: boolean = false;

  selectedClientId: string | null = null;
  clients: Client[] = [];
  loadingClients: boolean = false;

  selectedContractorTypeId: string | null = null;
  contractorTypes: ContractorType[] = [];
  loadingContractorTypes: boolean = false;

  loading: boolean = false;
  error: string | null = null;
  success: boolean = false;

  migrationResult: MigrateContractorRegistrationsResult | null = null;

  // Migration history
  migrationHistory: ContractorRegistrationMigrationProcessing[] = [];
  loadingHistory: boolean = false;

  private readonly onMigrationScroll = (event: Event): void => {
    if (!this.templateForPermitForm) {
      return;
    }
    if (event.target instanceof Node && this.elementRef.nativeElement.contains(event.target)) {
      return;
    }
    this.migrationDropdowns?.forEach(dropdown => {
      if (dropdown.open) {
        dropdown.close();
      }
    });
  };

  constructor(
    private _dataSvc: DataService,
    private _context: WorkflowContextService,
    private elementRef: ElementRef<HTMLElement>,
    private layoutService: LayoutService
  ) {}

  @HostBinding('class.janus-layout')
  get templateForPermitForm(): boolean {
    return this.layoutService.currentLayoutState;
  }

  get jurisdictionDropdownData(): CommonDropdownData {
    return {
      options: this.jurisdictionOptions,
      selectedValue: this.selectedClientId,
      placeholder: this.loadingClients ? 'Loading jurisdictions...' : 'Select jurisdiction',
      disabled: this.loadingClients,
      required: true,
      lockParentScroll: false,
      closeOnScroll: false,
      style: { width: '100%' }
    };
  }

  get contractorTypeDropdownData(): CommonDropdownData {
    return {
      options: this.contractorTypeOptions,
      selectedValue: this.selectedContractorTypeId,
      placeholder: this.loadingContractorTypes
        ? 'Loading contractor types...'
        : this.selectedClientId ? 'Select contractor type' : 'Select a jurisdiction first',
      disabled: !this.selectedClientId || this.loadingContractorTypes,
      required: true,
      lockParentScroll: false,
      closeOnScroll: false,
      style: { width: '100%' }
    };
  }

  ngOnInit() {
    this.historyColumns = [
      { key: 'createdOn', label: 'Date', cellTemplate: this.historyDate, width: '150px' },
      { key: 'contractorTypeName', label: 'Contractor Type', cellTemplate: this.historyType, width: '180px' },
      { key: 'status', label: 'Status', cellTemplate: this.historyStatus, width: '150px' },
      { key: 'contractorsCreated', label: 'Contractors Created', cellTemplate: this.historyCreated, width: '135px' },
      { key: 'contractorsUpdated', label: 'Contractors Updated', cellTemplate: this.historyUpdated, width: '135px' },
      { key: 'registrationsCreated', label: 'Registrations Created', cellTemplate: this.historyRegistrationsCreated, width: '145px' },
      { key: 'registrationsSkipped', label: 'Registrations Skipped', cellTemplate: this.historyRegistrationsSkipped, width: '145px' },
      { key: 'migratedBy', label: 'Run By', cellTemplate: this.historyRunBy, width: '140px' },
      { key: 'warningsJson', label: 'Warnings', cellTemplate: this.historyWarnings, width: '160px' }
    ].map(column => ({ ...column, headerType: 'text', cellType: 'template', sortable: false, hideMenu: true }));
    document.addEventListener('scroll', this.onMigrationScroll, true);
    this.uploadKey = this.generateId();
    this.loadClients();
  }

  ngOnDestroy(): void {
    document.removeEventListener('scroll', this.onMigrationScroll, true);
  }

  loadClients() {
    this.loadingClients = true;
    this._dataSvc.getClients().subscribe({
      next: clients => {
        this.clients = clients.sort((a, b) => a.name.localeCompare(b.name));
        this.jurisdictionOptions = this.clients.map(client => ({ label: client.name, value: client.id }));
        this.loadingClients = false;
      },
      error: err => {
        this.error =
          'Failed to load jurisdictions: ' + (err.error?.message || err.message);
        this.loadingClients = false;
      }
    });
  }

  onClientChange() {
    this.selectedContractorTypeId = null;
    this.contractorTypes = [];
    this.contractorTypeOptions = [];
    this.migrationHistory = [];
    if (!this.selectedClientId) {
      return;
    }

    this.loadingContractorTypes = true;
    this._dataSvc.getContractorTypes(this.selectedClientId).subscribe({
      next: types => {
        this.contractorTypes = (types ?? []).sort((a, b) =>
          (a.name ?? '').localeCompare(b.name ?? '')
        );
        this.contractorTypeOptions = this.contractorTypes.map(type => ({ label: type.name, value: type.id }));
        this.loadingContractorTypes = false;
      },
      error: err => {
        this.error =
          'Failed to load contractor types: ' + (err.error?.message || err.message);
        this.loadingContractorTypes = false;
      }
    });

    this.loadHistory();
  }

  loadHistory() {
    if (!this.selectedClientId) {
      return;
    }
    this.loadingHistory = true;
    this._dataSvc.getContractorRegistrationMigrationProcessings(this.selectedClientId).subscribe({
      next: records => {
        this.migrationHistory = records ?? [];
        this.loadingHistory = false;
      },
      error: () => { this.loadingHistory = false; }
    });
  }

  onFileUploaded(response: UploadResponse) {
    this.migrationFilePath = response.path;
    this.migrationFileUploaded = true;
    this.error = null;
    this.migrationResult = null;
  }

  onFileDeleted(_document: Document) {
    this.migrationFilePath = null;
    this.migrationFileUploaded = false;
    this.migrationResult = null;
  }

  migrate() {
    if (this.loading) {
      return;
    }

    if (!this.migrationFileUploaded) {
      this.error = 'Please upload a contractor registration migration file.';
      return;
    }
    if (!this.selectedClientId) {
      this.error = 'Please select a jurisdiction.';
      return;
    }
    if (!this.selectedContractorTypeId) {
      this.error = 'Please select a contractor type.';
      return;
    }

    this.loading = true;
    this.error = null;
    this.success = false;
    this.migrationResult = null;

    this._dataSvc
      .migrateContractorRegistrations(
        this.migrationFilePath!,
        this.selectedClientId,
        this.selectedContractorTypeId
      )
      .subscribe({
        next: result => {
          this.loading = false;
          this.success = true;
          this.migrationResult = result;
          this.loadHistory();
        },
        error: err => {
          this.loading = false;
          this.error =
            'Contractor registration migration failed: ' +
            (err.error?.message || err.message);
          this.loadHistory();
        }
      });
  }

  dismissError(): void {
    this.error = null;
  }

  runAnotherMigration(): void {
    this.success = false;
    this.migrationResult = null;
    this.selectedContractorTypeId = null;
  }

  getWarnings(record: ContractorRegistrationMigrationProcessing): string[] {
    if (!record.warningsJson) {
      return [];
    }
    try { return JSON.parse(record.warningsJson); } catch { return []; }
  }

  private generateId(): string {
    return Math.random().toString(36).substring(2, 11);
  }
}

